import { motion } from "framer-motion";

/**
 * Shimmer animation for skeleton loading states.
 * Use these when transitioning from demo data to live API calls.
 */

const shimmer = "relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent";

export function SkeletonLine({ width = "100%", height = "h-4" }: { width?: string; height?: string }) {
  return (
    <div className={`${height} rounded-md bg-[var(--color-cream-dark)]/60 ${shimmer}`} style={{ width }} />
  );
}

export function SkeletonCircle({ size = "w-10 h-10" }: { size?: string }) {
  return <div className={`${size} rounded-full bg-[var(--color-cream-dark)]/60 ${shimmer}`} />;
}

export function SkeletonCard() {
  return (
    <div className="card-elevated p-6 space-y-4 animate-pulse">
      <div className="flex items-center gap-3">
        <SkeletonCircle />
        <div className="flex-1 space-y-2">
          <SkeletonLine width="60%" />
          <SkeletonLine width="40%" height="h-3" />
        </div>
      </div>
      <div className="space-y-2">
        <SkeletonLine />
        <SkeletonLine width="85%" />
        <SkeletonLine width="70%" />
      </div>
    </div>
  );
}

export function SkeletonCardGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
        >
          <SkeletonCard />
        </motion.div>
      ))}
    </div>
  );
}

export function SkeletonDetailPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12 space-y-8 animate-pulse">
      {/* Back button */}
      <SkeletonLine width="80px" height="h-5" />
      {/* Title */}
      <div className="space-y-3">
        <SkeletonLine width="30%" height="h-3" />
        <SkeletonLine width="70%" height="h-8" />
        <SkeletonLine width="50%" height="h-4" />
      </div>
      {/* Content */}
      <div className="space-y-3 pt-4">
        <SkeletonLine />
        <SkeletonLine width="95%" />
        <SkeletonLine width="88%" />
        <SkeletonLine width="92%" />
        <SkeletonLine width="75%" />
      </div>
      <div className="space-y-3 pt-2">
        <SkeletonLine />
        <SkeletonLine width="90%" />
        <SkeletonLine width="80%" />
      </div>
      {/* Sources */}
      <div className="pt-4 space-y-3">
        <SkeletonLine width="120px" height="h-3" />
        <div className="flex gap-2">
          <SkeletonLine width="150px" height="h-8" />
          <SkeletonLine width="130px" height="h-8" />
          <SkeletonLine width="160px" height="h-8" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonHero() {
  return (
    <div className="bg-[var(--color-midnight)] py-16 md:py-20 animate-pulse">
      <div className="max-w-4xl mx-auto px-4 text-center space-y-4">
        <SkeletonCircle size="w-10 h-10 mx-auto" />
        <SkeletonLine width="50%" height="h-10" />
        <SkeletonLine width="70%" height="h-4" />
      </div>
    </div>
  );
}

export function SkeletonTimeline({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex gap-4 animate-pulse">
          <div className="flex flex-col items-center">
            <SkeletonCircle size="w-4 h-4" />
            <div className="w-0.5 h-full bg-[var(--color-cream-dark)]/40" />
          </div>
          <div className="flex-1 space-y-2 pb-6">
            <SkeletonLine width="100px" height="h-3" />
            <SkeletonLine width="60%" height="h-5" />
            <SkeletonLine width="90%" />
            <SkeletonLine width="75%" />
          </div>
        </div>
      ))}
    </div>
  );
}
